import * as React from 'react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { RecordContextProvider, useGetIdentity, useGetOne, useNotify } from 'react-admin';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { supabase } from '../../lib/supabaseClient';
import { OwnerSelector } from '../../components/OwnerSelector';
import { LeadDrawer } from '../LeadShow';
import { QualifyLeadDialog, formatPhone } from '../QualifyLeadDialog';
import { DISQUALIFY_REASONS } from '../disqualifyReasons';
import { disqualifyLeads, markNotALead } from './triageActions';
import {
    CleanupSubgroup,
    MarketFilter,
    OwnerFilter,
    Pill,
    PillTone,
    TriageRow,
    TypeFilter,
    aiPill,
    contextLine,
    daysSince,
    daysSinceContact,
    formatAge,
    groupTriage,
    isLikelyNotALead,
    isLikelyQualified,
    isOverdue,
    matchesFilters,
    statusWord,
    timelinePill,
    typePill,
} from './triageModel';

const PAGE = 10;

/** Supabase errors are plain objects with a message, not Error instances. */
function errorMessage(e: unknown, fallback: string): string {
    const message = (e as { message?: unknown } | null)?.message;
    return typeof message === 'string' && message ? message : fallback;
}
const TRIAGE_QUERY_KEY = 'leadsTriage';

async function fetchTriage(): Promise<TriageRow[]> {
    const { data, error } = await supabase.rpc('get_leads_triage');
    if (error) throw error;
    return (data ?? []) as TriageRow[];
}

/**
 * The /leads Triage tab (T-208): every lead that needs a decision from us, in
 * Bram's order — qualified leads still open, new forms nobody has acted on,
 * long calls that were never qualified, and calls to clean up in bulk.
 */
export function LeadsTriage() {
    const queryClient = useQueryClient();
    const notify = useNotify();
    const { identity } = useGetIdentity();
    const { data: rows = [], isLoading, error } = useQuery(TRIAGE_QUERY_KEY, fetchTriage);

    const [owner, setOwner] = useState<OwnerFilter>('everyone');
    const [market, setMarket] = useState<MarketFilter>('all');
    const [type, setType] = useState<TypeFilter>('all');
    const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);
    const [qualifyRow, setQualifyRow] = useState<TriageRow | null>(null);
    const [decision, setDecision] = useState<{ ids: string[]; allowNotALead: boolean } | null>(null);

    const now = useMemo(() => new Date(), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
    const myId = identity?.id ? String(identity.id) : null;
    const groups = useMemo(
        () => groupTriage(rows.filter(r => matchesFilters(r, { owner, market, type }, myId)), now),
        [rows, owner, market, type, myId, now],
    );

    const refetch = () => void queryClient.invalidateQueries(TRIAGE_QUERY_KEY);

    const runNotALead = async (ids: string[]) => {
        try {
            const n = await markNotALead(ids);
            notify(n === 1 ? 'Marked as not a lead' : `${n} leads marked as not a lead`, { type: 'success' });
            if (n < ids.length) notify(`${ids.length - n} skipped: someone already decided on them`, { type: 'info' });
        } catch (e) {
            notify(errorMessage(e, 'Could not update the leads'), { type: 'error' });
        }
        refetch();
    };

    if (isLoading) return <p className="p-4 text-sm text-muted-foreground">Loading triage…</p>;
    if (error) {
        return (
            <p className="p-4 text-sm text-red-700">
                Could not load the triage list: {(error as Error).message}
            </p>
        );
    }

    const qualifiedOpenCount = groups.formStarted.length + groups.noForm.length;

    return (
        <div className="cleo-ui flex flex-col gap-4 p-4">
            <nav className="grid grid-cols-2 overflow-hidden rounded-lg border sm:grid-cols-5" aria-label="Sections">
                <StripTile
                    hot={groups.deathNoForm.length > 0}
                    label="Death occurred, no form"
                    value={groups.deathNoForm.length}
                    sub="chase today"
                    target="triage-qualified"
                />
                <StripTile
                    label="Qualified, open"
                    value={qualifiedOpenCount}
                    sub={`${groups.noForm.length} no form · ${groups.formStarted.length} form started`}
                    target="triage-qualified"
                />
                <StripTile
                    label="New forms not actioned"
                    value={groups.newForms.length}
                    sub={`${groups.newForms.filter(r => !r.owner_id).length} with no owner`}
                    target="triage-forms"
                />
                <StripTile
                    label="3+ min call, not qualified"
                    value={groups.longCalls.length}
                    sub={stripSub(groups.longCalls, `${groups.longCalls.filter(r => r.has_gclid).length} from Google Ads`)}
                    target="triage-long-calls"
                />
                <StripTile
                    label="Calls to clean up"
                    value={groups.cleanup.length}
                    sub={stripSub(groups.cleanup, `${groups.cleanup.filter(r => (daysSince(r.opened_at, now) ?? 0) > 3).length} older than 3 days`)}
                    target="triage-cleanup"
                />
            </nav>

            <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Owner</span>
                <Chips
                    value={owner}
                    onChange={setOwner}
                    options={[['everyone', 'Everyone'], ['mine', 'Mine'], ['none', 'No owner']]}
                />
                <span className="mx-1 h-4 w-px bg-border" />
                <span className="text-muted-foreground">Market</span>
                <Chips value={market} onChange={setMarket} options={[['all', 'All'], ['QC', 'QC'], ['ON', 'ON']]} />
                <span className="mx-1 h-4 w-px bg-border" />
                <span className="text-muted-foreground">Type</span>
                <Chips
                    value={type}
                    onChange={setType}
                    options={[['all', 'All'], ['at_need', 'At-need'], ['pre_need', 'Pre-need']]}
                />
            </div>

            {/* 1 · Qualified, open */}
            <Section
                id="triage-qualified"
                title="1 · Qualified, open"
                count={groups.deathNoForm.length + qualifiedOpenCount}
                rule="qualified in the last 90 days, contract not signed"
            >
                <SubHeader hot label="Death occurred, no arrangement form" count={groups.deathNoForm.length} />
                <RowList
                    rows={groups.deathNoForm}
                    empty="Done for today. Every family whose person has died has started the arrangement form."
                    render={r => (
                        <QualifiedRow key={r.lead_id} row={r} now={now} onOpen={setDrawerLeadId} />
                    )}
                />
                <SubHeader label="Form started, not finished" count={groups.formStarted.length} />
                <RowList
                    rows={groups.formStarted}
                    empty="No arrangement form waiting."
                    render={r => <QualifiedRow key={r.lead_id} row={r} now={now} onOpen={setDrawerLeadId} />}
                />
                <NoFormGroup rows={groups.noForm} due={groups.noFormDue} now={now} onOpen={setDrawerLeadId} />
            </Section>

            {/* 2 · New forms */}
            <Section
                id="triage-forms"
                title="2 · New forms not actioned"
                count={groups.newForms.length}
                rule="web form in the last 30 days, still new, only the automatic reply went out"
            >
                <RowList
                    rows={groups.newForms}
                    empty="Done for today. Every form has a reply from a person or an outcome."
                    render={r => (
                        <Row key={r.lead_id} row={r} onOpen={setDrawerLeadId}>
                            <span className="text-xs font-medium">{statusWord(r)}</span>
                            <Age row={r} now={now} late={isOverdue(r, now)} />
                            <Owner row={r} />
                            <div className="flex justify-end gap-1">
                                <Button size="sm" className="h-7" onClick={() => setDrawerLeadId(r.lead_id)}>
                                    Reply
                                </Button>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label="More actions">
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="inbox-menu">
                                        <DropdownMenuItem onSelect={() => setQualifyRow(r)}>Qualify…</DropdownMenuItem>
                                        <DropdownMenuItem
                                            onSelect={() => setDecision({ ids: [r.lead_id], allowNotALead: false })}
                                        >
                                            Disqualify…
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onSelect={() => void runNotALead([r.lead_id])}>
                                            Not a lead
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onSelect={() => setDrawerLeadId(r.lead_id)}>
                                            Open lead
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </Row>
                    )}
                />
            </Section>

            {/* 3 · Long calls */}
            <Section
                id="triage-long-calls"
                title="3 · 3+ min call, not qualified"
                count={groups.longCalls.length}
                rule="answered call of 3 minutes or more in the last 30 days, never qualified"
            >
                <RowList
                    rows={groups.longCalls}
                    empty="Done for today. Every long call has an outcome."
                    render={r => (
                        <Row key={r.lead_id} row={r} onOpen={setDrawerLeadId} badge={r.has_gclid ? 'Google Ads' : undefined}>
                            <span className="text-xs font-medium">{statusWord(r)}</span>
                            <Age row={r} now={now} />
                            <Owner row={r} />
                            <div className="flex justify-end gap-1">
                                <Button size="sm" className="h-7" onClick={() => setQualifyRow(r)}>
                                    Qualify
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7"
                                    onClick={() => setDecision({ ids: [r.lead_id], allowNotALead: true })}
                                >
                                    Not qualified
                                </Button>
                            </div>
                        </Row>
                    )}
                />
            </Section>

            {/* 4 · Clean up */}
            <CleanupSection
                rows={groups.cleanup}
                now={now}
                onOpen={setDrawerLeadId}
                onNotALead={ids => runNotALead(ids)}
                onDisqualify={ids => setDecision({ ids, allowNotALead: false })}
            />

            <LeadDrawer leadId={drawerLeadId} open={!!drawerLeadId} onClose={() => { setDrawerLeadId(null); refetch(); }} />
            {qualifyRow && (
                <TriageQualifyDialog row={qualifyRow} onClose={() => { setQualifyRow(null); refetch(); }} />
            )}
            {decision && (
                <NotQualifiedDialog
                    ids={decision.ids}
                    allowNotALead={decision.allowNotALead}
                    onClose={() => setDecision(null)}
                    onNotALead={async () => { setDecision(null); await runNotALead(decision.ids); }}
                    onDisqualified={n => {
                        notify(n === 1 ? 'Lead disqualified' : `${n} leads disqualified`, { type: 'success' });
                        if (n < decision.ids.length) {
                            notify(`${decision.ids.length - n} skipped: someone already decided on them`, { type: 'info' });
                        }
                        setDecision(null);
                        refetch();
                    }}
                />
            )}
        </div>
    );
}

// ─── Layout pieces ───────────────────────────────────────────────────────────

function StripTile({
    label, value, sub, target, hot = false,
}: { label: string; value: number; sub: string; target: string; hot?: boolean }) {
    return (
        <button
            type="button"
            onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className={cn(
                'flex flex-col items-start gap-0.5 border-r px-3.5 py-2.5 text-left last:border-r-0 hover:bg-muted/60',
                hot && 'bg-red-50 text-red-700 hover:bg-red-100',
            )}
        >
            <span className={cn('text-xs', hot ? 'text-red-700' : 'text-muted-foreground')}>{label}</span>
            <span className="text-2xl font-semibold leading-tight tabular-nums">{value}</span>
            <span className={cn('text-[11px]', hot ? 'text-red-700/80' : 'text-muted-foreground')}>{sub}</span>
        </button>
    );
}

function Chips<T extends string>({
    value, onChange, options,
}: { value: T; onChange: (v: T) => void; options: Array<[T, string]> }) {
    return (
        <div className="flex gap-1">
            {options.map(([v, label]) => (
                <button
                    key={v}
                    type="button"
                    onClick={() => onChange(v)}
                    className={cn(
                        'rounded-full border px-2.5 py-0.5 text-xs',
                        v === value ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
                    )}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

function Section({
    id, title, count, rule, extra, children,
}: { id: string; title: string; count: number; rule: string; extra?: React.ReactNode; children: React.ReactNode }) {
    return (
        <section id={id} className="scroll-mt-4 overflow-hidden rounded-lg border">
            <header className="flex flex-wrap items-center gap-2.5 border-b bg-muted/50 px-3.5 py-2.5">
                <h3 className="text-sm font-semibold">{title}</h3>
                <span className="rounded-full border bg-background px-2 text-xs font-semibold tabular-nums">{count}</span>
                <span className="text-xs text-muted-foreground">{rule}</span>
                {extra && <span className="ml-auto">{extra}</span>}
            </header>
            {children}
        </section>
    );
}

function SubHeader({ label, count, hot = false, children }: { label: string; count: number; hot?: boolean; children?: React.ReactNode }) {
    return (
        <div
            className={cn(
                'flex items-center gap-2 border-b px-3.5 py-1.5 text-[11px] uppercase tracking-wide',
                hot ? 'bg-red-50 text-red-700' : 'text-muted-foreground',
            )}
        >
            <span>{label}</span>
            <span className="tabular-nums">· {count}</span>
            {children}
        </div>
    );
}

function DoneLine({ text }: { text: string }) {
    return (
        <div className="flex items-center gap-2 border-b px-3.5 py-3 text-[13px] text-muted-foreground last:border-b-0">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="flex-none text-emerald-700">
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M5 8.2l2 2 4-4.4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{text}</span>
        </div>
    );
}

/** A list that shows ten rows, then "Show N more". */
function RowList({
    rows, empty, render,
}: { rows: TriageRow[]; empty: string; render: (r: TriageRow) => React.ReactNode }) {
    const [limit, setLimit] = useState(PAGE);
    if (rows.length === 0) return <DoneLine text={empty} />;
    return (
        <>
            {rows.slice(0, limit).map(render)}
            {rows.length > limit && (
                <ShowMore onClick={() => setLimit(l => l + 50)}>{showMoreLabel(rows.length - limit)}</ShowMore>
            )}
        </>
    );
}

function showMoreLabel(rest: number): string {
    return rest > 50 ? `Show 50 more (${rest} left)` : `Show ${rest} more`;
}

function ShowMore({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
    return (
        <button type="button" onClick={onClick} className="w-full border-b px-3.5 py-2 text-left text-xs text-primary last:border-b-0 hover:bg-muted/50">
            {children}
        </button>
    );
}

function leadName(row: TriageRow): string {
    return row.contact_name?.trim() || formatPhone(row.contact_phone) || 'Unknown caller';
}

/**
 * One line per lead: "Lead" label, name, one line of context, then the
 * section's own cells (status, time, owner, actions).
 */
function Row({
    row, onOpen, leading, badge, hot = false, children,
}: {
    row: TriageRow;
    onOpen: (id: string) => void;
    leading?: React.ReactNode;
    badge?: string;
    hot?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div
            className={cn(
                'grid min-h-[46px] grid-cols-[auto_minmax(130px,1fr)_minmax(0,2.2fr)_104px_116px_128px_180px] items-center gap-3 border-b px-3.5 py-2 last:border-b-0 hover:bg-muted/40',
                hot && 'shadow-[inset_3px_0_0_var(--color-red-600)]',
                leading && 'grid-cols-[18px_auto_minmax(130px,1fr)_minmax(0,2.2fr)_104px_116px_128px_180px]',
            )}
        >
            {leading}
            <span className="rounded border px-1.5 text-[11px] font-medium text-muted-foreground">Lead</span>
            <button
                type="button"
                onClick={() => onOpen(row.lead_id)}
                className="truncate text-left text-sm font-medium hover:underline"
                title={leadName(row)}
            >
                {leadName(row)}
            </button>
            <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
                {badge && <span className="flex-none rounded border px-1.5 text-[11px]">{badge}</span>}
                <RowPill pill={aiPill(row)} />
                <RowPill pill={typePill(row)} />
                <RowPill pill={timelinePill(row)} />
                <span className="truncate" title={contextLine(row)}>{contextLine(row) || '—'}</span>
            </span>
            {children}
        </div>
    );
}

const PILL_TONES: Record<PillTone, string> = {
    red: 'bg-red-600 text-white',
    green: 'bg-green-700 text-white',
    amber: 'bg-orange-500 text-white',
    blue: 'bg-sky-600 text-white',
    grey: 'bg-muted text-foreground',
    accent: 'bg-primary text-primary-foreground',
};

/** A small Type / Timeline pill; nothing when the value is unknown. */
function RowPill({ pill }: { pill: Pill | null }) {
    if (!pill) return null;
    return (
        <span
            className={cn('flex-none whitespace-nowrap rounded-full px-2 py-px text-[11px] font-medium', PILL_TONES[pill.tone])}
            title={pill.title}
        >
            {pill.label}
        </span>
    );
}

/** When it came in, and the last contact the rows are sorted by. */
function Age({ row, now, late = false }: { row: TriageRow; now: Date; late?: boolean }) {
    return (
        <span className="text-right text-[11px] leading-tight tabular-nums text-muted-foreground">
            opened <span className={cn(late && 'font-medium text-amber-700')}>{formatAge(daysSince(row.opened_at, now))}</span>
            <br />
            last contact {row.last_contact_at ? formatAge(daysSince(row.last_contact_at, now)) : 'never'}
        </span>
    );
}

/** The shared owner chip (same as the lead page's), on a row. */
function Owner({ row }: { row: TriageRow }) {
    const record = useMemo(() => ({ id: row.lead_id, owner_id: row.owner_id }), [row.lead_id, row.owner_id]);
    return (
        <RecordContextProvider value={record}>
            <span className="flex min-w-0 justify-start overflow-hidden">
                <OwnerSelector resource="leads" noun="lead" compact />
            </span>
        </RecordContextProvider>
    );
}

function QualifiedRow({ row, now, onOpen }: { row: TriageRow; now: Date; onOpen: (id: string) => void }) {
    const hot = row.subgroup === 'death_no_form';
    const late = isOverdue(row, now);
    return (
        <Row row={row} onOpen={onOpen} hot={hot}>
            <span className={cn('text-xs font-medium', hot && 'text-red-700')}>{statusWord(row)}</span>
            <span className="text-[11px] leading-tight text-muted-foreground tabular-nums">
                qualified <b className={cn('font-medium text-foreground', hot && 'text-red-700')}>{formatAge(daysSince(row.qualified_at, now))}</b>
                <br />
                last contact{' '}
                <span className={cn(late && 'font-medium text-amber-700')}>
                    {row.last_contact_at ? formatAge(daysSinceContact(row, now)) : 'never'}
                </span>
            </span>
            <Owner row={row} />
            <div className="flex justify-end">
                {row.subgroup === 'form_started' && row.funeral_id ? (
                    <Button size="sm" className="h-7" asChild>
                        <Link to={`/funerals/${row.funeral_id}/show`}>Help finish</Link>
                    </Button>
                ) : (
                    <Button size="sm" className="h-7" onClick={() => onOpen(row.lead_id)}>
                        Follow up
                    </Button>
                )}
            </div>
        </Row>
    );
}

/**
 * "No form yet" is mostly stale, so it opens collapsed: only the leads past
 * their contact limit show until someone expands it.
 */
function NoFormGroup({
    rows, due, now, onOpen,
}: { rows: TriageRow[]; due: TriageRow[]; now: Date; onOpen: (id: string) => void }) {
    const [expanded, setExpanded] = useState(false);
    const shown = expanded ? rows : due;
    return (
        <>
            <SubHeader label="No form yet: imminent, near future, pre-need" count={rows.length}>
                <button
                    type="button"
                    onClick={() => setExpanded(e => !e)}
                    className="ml-auto normal-case tracking-normal text-primary hover:underline"
                >
                    {expanded ? `Show only the ${due.length} past their contact limit` : `${due.length} past their contact limit · show all ${rows.length}`}
                </button>
            </SubHeader>
            <RowList
                key={expanded ? 'all' : 'due'}
                rows={shown}
                empty={expanded ? 'No qualified lead without a form.' : 'Everyone without a form has been contacted within their limit.'}
                render={r => <QualifiedRow key={r.lead_id} row={r} now={now} onOpen={onOpen} />}
            />
        </>
    );
}

type CleanupFilter = CleanupSubgroup | 'all' | 'likely_qualified' | 'likely_not_a_lead';

const CLEANUP_FILTERS: Array<[CleanupFilter, string]> = [
    ['all', 'All'],
    ['likely_qualified', 'Likely qualified'],
    ['likely_not_a_lead', 'Likely not a lead'],
    ['short_call', 'Short call'],
    ['never_spoken', 'Never spoken to'],
    ['duplicate', 'Already qualified on another lead'],
];

function matchesCleanupFilter(row: TriageRow, filter: CleanupFilter): boolean {
    if (filter === 'all') return true;
    if (filter === 'likely_qualified') return isLikelyQualified(row);
    if (filter === 'likely_not_a_lead') return isLikelyNotALead(row);
    return row.subgroup === filter;
}

/** "5 likely qualified" when the AI flagged any, otherwise the tile's usual line. */
function stripSub(rows: TriageRow[], fallback: string): string {
    const n = rows.filter(isLikelyQualified).length;
    return n > 0 ? `${n} likely qualified` : fallback;
}

function CleanupSection({
    rows, now, onOpen, onNotALead, onDisqualify,
}: {
    rows: TriageRow[];
    now: Date;
    onOpen: (id: string) => void;
    onNotALead: (ids: string[]) => Promise<void>;
    onDisqualify: (ids: string[]) => void;
}) {
    const [filter, setFilter] = useState<CleanupFilter>('all');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [limit, setLimit] = useState(PAGE * 2);
    const [busy, setBusy] = useState(false);

    const visible = rows.filter(r => matchesCleanupFilter(r, filter));
    const shown = visible.slice(0, limit);
    // Drop selections that left the list (decided elsewhere, or filtered out).
    const selectedIds = visible.filter(r => selected.has(r.lead_id)).map(r => r.lead_id);
    const allShownSelected = shown.length > 0 && shown.every(r => selected.has(r.lead_id));

    const toggle = (id: string) =>
        setSelected(s => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    const toggleAll = () =>
        setSelected(allShownSelected ? new Set() : new Set(shown.map(r => r.lead_id)));

    const notALead = async () => {
        setBusy(true);
        await onNotALead(selectedIds);
        setSelected(new Set());
        setBusy(false);
    };

    return (
        <Section
            id="triage-cleanup"
            title="4 · Calls to clean up"
            count={rows.length}
            rule="open call leads in the last 30 days with no sign of a prospect · mark them in bulk"
        >
            {rows.length === 0 ? (
                <DoneLine text="All clean. Every call from the last 30 days is qualified, disqualified or not a lead." />
            ) : (
                <>
                    <div className="flex flex-wrap items-center gap-1.5 border-b px-3.5 py-2">
                        {CLEANUP_FILTERS.map(([v, label]) => {
                            const n = rows.filter(r => matchesCleanupFilter(r, v)).length;
                            // The AI chips stay out of the way until there are verdicts to show.
                            if (n === 0 && (v === 'likely_qualified' || v === 'likely_not_a_lead')) return null;
                            return (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => { setFilter(v); setSelected(new Set()); setLimit(PAGE * 2); }}
                                    className={cn(
                                        'rounded-full border px-2.5 py-0.5 text-xs',
                                        v === filter ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
                                    )}
                                >
                                    {label} · {n}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap items-center gap-2.5 border-b bg-primary/5 px-3.5 py-2 text-[13px]">
                        <input
                            type="checkbox"
                            aria-label="Select all shown"
                            checked={allShownSelected}
                            onChange={toggleAll}
                            className="h-[15px] w-[15px] accent-primary"
                        />
                        <b className="tabular-nums">{selectedIds.length} selected</b>
                        <span className="text-xs text-muted-foreground">of {visible.length}</span>
                        <span className="flex-1" />
                        <Button size="sm" variant="outline" className="h-7" disabled={!selectedIds.length || busy} onClick={() => onDisqualify(selectedIds)}>
                            Disqualify…
                        </Button>
                        <Button size="sm" className="h-7" disabled={!selectedIds.length || busy} onClick={() => void notALead()}>
                            Not a lead
                        </Button>
                    </div>
                    {shown.map(r => (
                        <Row
                            key={r.lead_id}
                            row={r}
                            onOpen={onOpen}
                            leading={
                                <input
                                    type="checkbox"
                                    aria-label={`Select ${leadName(r)}`}
                                    checked={selected.has(r.lead_id)}
                                    onChange={() => toggle(r.lead_id)}
                                    className="h-[15px] w-[15px] accent-primary"
                                />
                            }
                        >
                            <span className="text-xs text-muted-foreground">{statusWord(r)}</span>
                            <Age row={r} now={now} />
                            <Owner row={r} />
                            <span />
                        </Row>
                    ))}
                    {visible.length > limit && (
                        <ShowMore onClick={() => setLimit(l => l + 50)}>
                            {showMoreLabel(visible.length - limit)}
                        </ShowMore>
                    )}
                </>
            )}
        </Section>
    );
}

// ─── Dialogs ─────────────────────────────────────────────────────────────────

/** The lead page's Qualify dialog, fed the lead and its client. */
function TriageQualifyDialog({ row, onClose }: { row: TriageRow; onClose: () => void }) {
    const { data: lead } = useGetOne('leads', { id: row.lead_id });
    const { data: client } = useGetOne('clients', { id: row.client_id as number }, { enabled: !!row.client_id });
    const ready = !!lead && (!row.client_id || !!client);
    return (
        <QualifyLeadDialog
            open={ready}
            onClose={onClose}
            leadId={row.lead_id}
            contact={client ?? (row.contact_name ? { name: row.contact_name } : null)}
            lead={lead}
        />
    );
}

/**
 * "Not qualified" on one call, or "Disqualify…" on a batch: Not a lead (one
 * click) or a disqualify reason, the same list as the lead page.
 */
function NotQualifiedDialog({
    ids, allowNotALead, onClose, onNotALead, onDisqualified,
}: {
    ids: string[];
    allowNotALead: boolean;
    onClose: () => void;
    onNotALead: () => Promise<void>;
    onDisqualified: (n: number) => void;
}) {
    const notify = useNotify();
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const confirm = async () => {
        setSaving(true);
        try {
            onDisqualified(await disqualifyLeads(ids, reason, notes));
        } catch (e) {
            notify(errorMessage(e, 'Could not disqualify'), { type: 'error' });
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={o => !o && !saving && onClose()}>
            <DialogContent className="cleo-ui max-w-[460px]">
                <DialogHeader>
                    <DialogTitle>
                        {allowNotALead ? 'Not qualified' : ids.length === 1 ? 'Disqualify lead' : `Disqualify ${ids.length} leads`}
                    </DialogTitle>
                </DialogHeader>
                {allowNotALead && (
                    <div className="flex items-center justify-between gap-3 rounded-md border p-3">
                        <span className="text-[13px]">
                            <b className="font-medium">Not a lead</b>
                            <br />
                            <span className="text-muted-foreground">Hospital, vendor, existing family, wrong number</span>
                        </span>
                        <Button size="sm" disabled={saving} onClick={() => void onNotALead()}>
                            Not a lead
                        </Button>
                    </div>
                )}
                <fieldset className="flex flex-col gap-1.5">
                    <legend className="mb-1 text-[13px] font-medium">
                        {allowNotALead ? 'Or disqualify, with a reason' : 'Reason'}
                    </legend>
                    {DISQUALIFY_REASONS.map(([value, label]) => (
                        <label key={value} className="flex items-center gap-2 text-[13px]">
                            <input
                                type="radio"
                                name="triage-disqualify-reason"
                                value={value}
                                checked={reason === value}
                                onChange={() => setReason(value)}
                                className="accent-primary"
                            />
                            {label}
                        </label>
                    ))}
                </fieldset>
                <Textarea
                    placeholder={reason === 'other' ? 'Notes (required)' : 'Notes'}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                />
                <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        disabled={saving || !reason || (reason === 'other' && !notes.trim())}
                        onClick={() => void confirm()}
                    >
                        Disqualify
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
